import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, EmptyState, PageLayout } from '@sentinel/ui';
import { ConfirmDialog } from '../features/security/components/confirm-dialog';
import { ConnectedAppRow } from '../features/connected-apps/components/connected-app-row';
import type { ConnectedApp } from '../features/connected-apps/types';
import { useConnectedApps, useRevokeConnectedApp } from '../features/connected-apps/use-connected-apps';

const listStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.5rem',
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function displayName(app: ConnectedApp | null): string {
  return app?.client_name ?? app?.client_id ?? '';
}

export function ConnectedAppsPage() {
  const { t } = useTranslation('connected-apps');
  const query = useConnectedApps();
  const revoke = useRevokeConnectedApp();

  const [banner, setBanner] = useState<{ tone: 'error' | 'info'; message: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ConnectedApp | null>(null);

  const apps = query.data?.pages.flatMap((p) => p.items) ?? [];

  function handleRevokeClick(app: ConnectedApp) {
    setBanner(null);
    setRevokeTarget(app);
  }

  async function handleConfirmRevoke() {
    if (!revokeTarget) return;
    setBanner(null);
    const target = revokeTarget;
    try {
      await revoke.mutateAsync({ id: target.id });
      setRevokeTarget(null);
      setBanner({ tone: 'info', message: t('revoke_succeeded', { app: displayName(target) }) });
    } catch (e) {
      setRevokeTarget(null);
      const status = getStatus(e);
      if (status === 404) {
        // Someone else already removed it; the hook's invalidation refreshes
        // the list. Not an error the user caused.
        setBanner({ tone: 'info', message: t('revoke_already_gone') });
        return;
      }
      if (status === 403) {
        // Not a cancelled step-up: DELETE /auth/consents/{id} has no step-up
        // gate, so the only 403 it emits is the ownership check. The global
        // forbidden handler reports that; revoke_failed would wrongly invite a
        // retry.
        return;
      }
      setBanner({ tone: 'error', message: t('revoke_failed') });
    }
  }

  return (
    <>
      <PageLayout title={t('title')} subtitle={t('subtitle')}>
        {banner ? (
          <Alert type={banner.tone} role={banner.tone === 'error' ? 'alert' : 'status'}>
            {banner.message}
          </Alert>
        ) : null}

        <section data-testid="connected-apps">
          {query.isLoading ? (
            <p data-testid="connected-apps-loading">{t('loading')}</p>
          ) : query.error ? (
            <Alert type="error" role="alert" data-testid="connected-apps-error">
              {t('load_failed')}
            </Alert>
          ) : apps.length === 0 && !query.hasNextPage ? (
            <EmptyState
              testId="connected-apps-empty"
              title={t('empty_title')}
              description={t('empty_body')}
            />
          ) : (
            <>
              {apps.length > 0 ? (
                <ul style={listStyle} data-testid="connected-apps-list">
                  {apps.map((app) => (
                    <ConnectedAppRow
                      key={app.id}
                      app={app}
                      revoking={revoke.isPending && revokeTarget?.id === app.id}
                      onRevoke={handleRevokeClick}
                    />
                  ))}
                </ul>
              ) : null}
              {query.hasNextPage ? (
                <Button
                  type="button"
                  variant="secondary"
                  data-testid="connected-apps-load-more"
                  disabled={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                >
                  {t('load_more')}
                </Button>
              ) : null}
            </>
          )}
        </section>
      </PageLayout>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={t('revoke_confirm_title', { app: displayName(revokeTarget) })}
        subtitle={t('revoke_confirm_subtitle', { app: displayName(revokeTarget) })}
        submitLabel={t('revoke_confirm_submit')}
        cancelLabel={t('revoke_confirm_cancel')}
        submitting={revoke.isPending}
        variant="danger"
        onSubmit={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </>
  );
}

export const Route = createFileRoute('/account/connected-apps')({
  component: ConnectedAppsPage,
});
